import { ModalLayout as EditResearchAreasModalLayout } from '@/components'
import { Box, CircularProgress, Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { ProjectBasicInfoForm, ProjectBasicInfo } from '@/components/forms'
import {
  useProject,
  usePatchProject,
  useMutationWrapper,
  useLinkQuotationToProject,
} from '@/hooks'
import { StandardTimeType } from '@/types'

export type EditInformationModalProps = {
  projectId: number
}

export const EditInformationModal = ({
  projectId,
}: EditInformationModalProps): JSX.Element => {
  const { t } = useTranslation('projects')

  const { enqueueSnackbar } = useSnackbar()

  const { data: project, isLoading } = useProject({ id: projectId })

  const { mutateAsync: extendProject, isLoading: mutateInProcess } =
    useMutationWrapper(usePatchProject.bind(null, projectId))

  const linkQuotations = useLinkQuotationToProject()

  const handleSubmit = async ({
    projectNumber,
    name,
    description,
    projectLeader,
    sharepointUrl,
    address,
    hasGisStatus,
    gisMap,
    status,
    isSmp,
    isOnlyPlanning,
    isFieldReportRequired,
    quotations,
    travelTimePerVisit,
    standardReportingTimes,
  }: ProjectBasicInfo) => {
    const projectExtendedData = {
      project_leader_id: projectLeader?.value || null,
      project_number: projectNumber,
      name,
      description,
      is_smp: isSmp,
      is_only_planning: isOnlyPlanning,
      is_field_report_required: isFieldReportRequired,
      sharepoint_url: sharepointUrl,
      address,
      travel_time_per_visit: travelTimePerVisit,
      is_in_gis: hasGisStatus,
      gis_map: gisMap,
      status,
      standard_times: standardReportingTimes.map((srt) => ({
        id: srt.id,
        value_per_visit: srt.value_per_visit,
        specie_id: srt.selectedSpecie?.value || null,
        type: StandardTimeType.REPORTING,
      })),
    }

    try {
      const project = await extendProject(projectExtendedData, {
        successMessageKey: 'projects:detail.successEdit',
      })

      await linkQuotations(project, quotations)
    } catch (e) {
      enqueueSnackbar(`${t('errors:api.execute')}: ${e}`, {
        variant: 'error',
      })
    }
  }

  if (isLoading || !project)
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <CircularProgress size="5rem" color="secondary" />
      </Stack>
    )

  return (
    <EditResearchAreasModalLayout title={t('detail.editProjectInfo')}>
      <Box width={{ md: 923, sm: 'auto' }} maxHeight="60vh" overflow="auto">
        <ProjectBasicInfoForm
          project={project}
          onSubmit={handleSubmit}
          controls={
            <LoadingButton
              variant="contained"
              color="secondary"
              type="submit"
              loading={mutateInProcess}
            >
              <span>{t('common:buttons.saveChanges')}</span>
            </LoadingButton>
          }
          isReadOnlyDate={true}
          hasStatusFields={true}
          showControlsOnDirty={true}
        />
      </Box>
    </EditResearchAreasModalLayout>
  )
}
